/**
 * Ambient street noise audio — lazy-loaded on-demand, non-blocking.
 */

let audio: HTMLAudioElement | null = null
let loadPromise: Promise<void> | null = null
let isPlaying = false
let audioUnlocked = false
let globalMuted = false
let shouldAutoPlay = false // Track if we want to play but audio isn't ready yet

const VOLUME = 0.2

/** Unlock ambient audio on iOS (must be called from user interaction). */
export async function unlockAmbient() {
    if (audioUnlocked) return
    audioUnlocked = true

    // Use silent audio to unlock the audio context
    const silentAudio = new Audio()
    silentAudio.src =
        "data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV"
    try {
        await silentAudio.play()
        silentAudio.pause()
    } catch {
        // Ignore errors
    }
}

/**
 * Start loading ambient audio in the background.
 * Non-blocking - doesn't delay page load.
 */
function lazyLoadAmbient() {
    if (loadPromise) return loadPromise

    loadPromise = new Promise<void>((resolve) => {
        audio = new Audio("/ambient.mp3")
        audio.loop = true
        audio.volume = globalMuted ? 0 : VOLUME
        audio.preload = "none" // Don't preload until needed

        audio.addEventListener(
            "canplaythrough",
            () => {
                resolve()
                // If we tried to play before loading finished, play now
                if (shouldAutoPlay && isPlaying) {
                    audio!.play().catch(() => {
                        console.warn("Ambient audio autoplay blocked")
                    })
                }
            },
            { once: true }
        )

        audio.addEventListener(
            "error",
            () => {
                console.warn("Ambient audio failed to load")
                resolve() // don't block playback if audio fails
            },
            { once: true }
        )

        // Start loading
        audio.load()
    })

    return loadPromise
}

/** Set global mute state for ambient audio */
export function setGlobalMuteAmbient(muted: boolean) {
    globalMuted = muted
    if (audio) {
        audio.volume = globalMuted ? 0 : VOLUME
    }
}

/** Get current global mute state */
export function isGlobalMutedAmbient(): boolean {
    return globalMuted
}

/**
 * Play ambient audio. Non-blocking - starts load if needed, plays when ready.
 * Does not delay simulation start.
 */
export function playAmbient() {
    if (isPlaying) return
    isPlaying = true
    shouldAutoPlay = true

    if (!loadPromise) {
        // Start loading now
        lazyLoadAmbient()
    }

    // If already loaded, play immediately
    if (audio && loadPromise) {
        loadPromise.then(() => {
            if (!audio || !isPlaying) return
            audio.volume = globalMuted ? 0 : VOLUME
            audio.play().catch(() => {
                console.warn("Ambient audio autoplay blocked")
            })
        })
    }
}

/** Pause ambient audio. */
export function pauseAmbient() {
    isPlaying = false
    shouldAutoPlay = false
    if (audio && !audio.paused) {
        audio.pause()
    }
}

/** Stop and reset ambient audio. */
export function stopAmbient() {
    isPlaying = false
    shouldAutoPlay = false
    if (audio) {
        audio.pause()
        audio.currentTime = 0
    }
}
